import { Config } from 'config/envConfig';
import React, { useEffect, useState, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { IMAGES } from '../../enums';
import { LandingPageButton } from '@quyva/shared-ui';
import { getUrl } from '../../components/pdfDisplay/utils';

const ChatSteps = forwardRef((props, ref) => {
    const [selectedStep, setSelectedStep] = useState(1);
    const steps = [
        {
            number: 1,
            name: 'Connect Your Data Sources',
            description:
                'Upload PDFs, DOCX files, connect databases (MySQL, PostgreSQL, MS SQL), or integrate with email, web crawlers, and cloud storage.',
        },
        {
            number: 2,
            name: 'Chat & Automate',
            description:
                'Ask questions in natural language, create custom AI agents for automated workflows, and get intelligent contextual answers with source verification.',
        },
        {
            number: 3,
            name: 'Extract & Report',
            description:
                'Extract data in any structured format (JSON, CSV, Excel) and convert insights into professional PDF reports, dashboards, or custom documents.',
        },
    ];

    const handleStepClick = (stepNumber) => {
        setSelectedStep(stepNumber);
    };

    const handleClick = (url) => {
        const host = Config.HOST;
        const port = Config.PORT;
        const useSecuredConnection = Config.USE_SECURED_CONNECTION;
        if (useSecuredConnection) {
            window.open(getUrl(`http://${host}${url}`), '_self');
        } else {
            window.open(getUrl(`http://${host}:${port}${url}`), '_self');
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setSelectedStep((prevStep) => (prevStep === 3 ? 1 : prevStep + 1));
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    return (
        <section ref={ref} className='py-24 bg-landing-slate-50'>
            <div className='max-w-7xl mx-auto px-6'>
                <div className='text-center mb-16'>
                    <span className='text-landing-blue-500 font-bold tracking-wide uppercase text-sm'>
                        How It Works
                    </span>
                    <h2 className='text-3xl md:text-4xl font-bold text-landing-slate-900 mt-2 mb-6'>
                        Get Started in Three Simple Steps
                    </h2>
                    <p className='text-xl text-landing-slate-600 max-w-2xl mx-auto'>
                        Transform how you interact with your documents and data
                        in minutes
                    </p>
                </div>

                <div className='flex flex-col lg:flex-row items-center gap-12'>
                    {/* Steps List */}
                    <div className='w-full lg:w-1/2'>
                        <div className='space-y-6'>
                            {steps.map((step) => (
                                <div
                                    key={step.number}
                                    onClick={() => handleStepClick(step.number)}
                                    className={`flex gap-4 p-6 rounded-xl cursor-pointer transition-all ${
                                        selectedStep === step.number
                                            ? 'bg-landing-white shadow-lg border-l-4 border-landing-blue-500'
                                            : 'bg-landing-white/50 hover:bg-landing-white hover:shadow-md'
                                    }`}
                                >
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            selectedStep === step.number
                                                ? 'bg-landing-blue-500 text-landing-white'
                                                : 'bg-landing-slate-200 text-landing-slate-600'
                                        }`}
                                    >
                                        {selectedStep > step.number ? (
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className='text-sm'
                                            />
                                        ) : (
                                            <span className='font-bold'>
                                                {step.number}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h3
                                            className={`font-bold text-lg mb-2 ${
                                                selectedStep === step.number
                                                    ? 'text-landing-slate-900'
                                                    : 'text-landing-slate-700'
                                            }`}
                                        >
                                            {step.name}
                                        </h3>
                                        <p className='text-landing-slate-600 text-sm leading-relaxed'>
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className='mt-8'>
                            <LandingPageButton
                                className='px-8 py-4 bg-landing-blue-500 text-landing-white rounded-lg font-bold hover:bg-landing-blue-600 transition shadow-lg shadow-landing-blue-600/20 inline-flex items-center gap-2'
                                onClick={() => handleClick('/contactUs')}
                                text='Contact Us'
                                icon={faEnvelope}
                            />
                        </div>
                    </div>

                    {/* Image Preview */}
                    <div className='w-full lg:w-1/2 flex justify-center'>
                        <div className='relative'>
                            <div className='absolute -inset-4 bg-landing-blue-500/10 rounded-2xl blur-xl'></div>
                            <div className='relative bg-landing-slate-800 p-3 rounded-2xl shadow-2xl'>
                                <img
                                    src={
                                        Config.PUBLIC_URL +
                                        `/ChatSteps/${
                                            selectedStep === 1
                                                ? IMAGES.CHAT_STEP_1
                                                : selectedStep === 2
                                                ? IMAGES.CHAT_STEP_2
                                                : IMAGES.CHAT_STEP_3
                                        }`
                                    }
                                    alt={`Step ${selectedStep}`}
                                    className='max-w-full rounded-xl'
                                    draggable='false'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});

export default ChatSteps;
